/**
 * B.2.b — Reads the Repancy portal role from Clerk's identity claims.
 *
 * Returns one of `operator`, `associate`, `admin`, or `null` (no role).
 * `operator` is the staff/admin role for Repancy team members (provisioning
 * portals, managing search admin). `associate` is the non-admin lawyer
 * role. `admin` is the legacy `repancy_admin` role kept for backward
 * compatibility.
 *
 * Reads the same shape Convex's `roleGuards.readRole` accepts:
 *   - flat `publicMetadata.role` string claim (preferred)
 *   - or a `publicMetadata.roles` array (Clerk Organizations style)
 *
 * Returned shape:
 *   - `role: 'operator' | 'associate' | 'admin' | null`
 *   - `isLoading: boolean`  — true while Clerk is hydrating.
 *
 * Use for role-aware UI ONLY. Server-side authorization MUST gate via
 * the Convex `requireOperator` / `requireAssociate` helpers — never trust
 * a client-side role claim for an actual write decision.
 */

import { useUser } from '@clerk/react';
import { useMemo } from 'react';

export type RepancyRole = 'operator' | 'associate' | 'admin';

const ROLE_LITERALS = {
  operator: 'repancy_operator',
  associate: 'repancy_associate',
  admin: 'repancy_admin',
} as const;

function classifyClaim(claim: string): RepancyRole | null {
  switch (claim) {
    case ROLE_LITERALS.operator:
      return 'operator';
    case ROLE_LITERALS.associate:
      return 'associate';
    case ROLE_LITERALS.admin:
      return 'admin';
    default:
      return null;
  }
}

/**
 * Pure helper exposed for testing. Returns the first recognised role from
 * the public-metadata blob, else null.
 */
export function readRoleFromPublicMetadata(
  meta: Record<string, unknown> | undefined,
): RepancyRole | null {
  if (meta === undefined) return null;
  const flat = meta.role;
  if (typeof flat === 'string') {
    const r = classifyClaim(flat);
    if (r !== null) return r;
  }
  const list = meta.roles;
  if (Array.isArray(list)) {
    for (const entry of list) {
      if (typeof entry !== 'string') continue;
      const r = classifyClaim(entry);
      if (r !== null) return r;
    }
  }
  return null;
}

export interface UseRepancyRole {
  role: RepancyRole | null;
  isLoading: boolean;
}

export function useRepancyRole(): UseRepancyRole {
  const { isLoaded, user } = useUser();
  const role = useMemo<RepancyRole | null>(() => {
    if (!isLoaded || user === null || user === undefined) return null;
    return readRoleFromPublicMetadata(
      user.publicMetadata as Record<string, unknown> | undefined,
    );
  }, [isLoaded, user]);
  return { role, isLoading: !isLoaded };
}
